import type { Entity } from '@platform/entities/entity.class';
import type { ColumnParams } from '@platform/shared/rpc';
import { DataType } from '@platform/shared/rpc';
import { localeSettings } from '@platform/shared/lib/datetime';
import { resolveReference } from '@platform/shared/helpers/resolve-entity';

export function getFormNotations(entity: typeof Entity) {
    return Object.entries(entity._formFields).reduce(
        (acc, [field, params]) => {
            if (field === 'id' || params?.skip) return acc;
            const struct = entity._struct?.[field];
            const selectedComponentInfo = struct ? selectComponent(entity, field, struct) : { component: 'QInput' };
            if (!struct && params?.component === undefined) {
                console.warn(`Selecting QInput for ${entity.name}.${field}, but you should specify it explicitly.`);
            }
            acc[field] = {
                component: params?.component || selectedComponentInfo.component,
                prop: field,
                required: params?.required ?? false,
                requiredFunc: params?.requiredFunc ?? null,
                rules: params?.rules || undefined,
                props: {
                    label: params?.label || params?.props?.label || struct.comment || field,
                    ...selectedComponentInfo.props,
                    ...params?.props,
                },
            };
            if (typeof params.componentFn === 'function') acc[field].componentFn = params.componentFn;
            if (params.boundProps) acc[field].boundProps = params.boundProps;
            if (params.meta) acc[field].meta = params.meta;
            if (params.layout) acc[field].layout = params.layout;
            return acc;
        },
        {} as Record<string, any>
    );
}

function selectComponent(
    entity: typeof Entity,
    field: string,
    struct: { dataType: DataType | [DataType] } & ColumnParams
): { component?: string; props?: Record<string, any> } {
    switch (struct.dataType) {
        case DataType.UUID: {
            return { component: 'FieldSelect', props: { ...resolveTableAndSchema(field, struct) } };
        }
        case DataType.DATE: {
            return { component: 'DatePopup' };
        }
        case DataType.JSON: {
            return { props: { ...resolveTableAndSchema(field, struct) } };
        }
        case DataType.TIMESTAMP: {
            return { component: 'DatePopup', props: { maskInput: localeSettings.dateTimeMask, formatDateTime: true } };
        }
        default:
            return { component: 'QInput' };
    }
}

export function resolveTableAndSchema(field: string, struct: { dataType: DataType | [DataType] } & ColumnParams) {
    let tableName: string | undefined, schemaName: string | undefined;
    if (struct.ref) {
        if (struct.ref.indexOf('.') !== -1) [schemaName, tableName] = struct.ref.split('.', 2);
        else tableName = struct.ref;
    } else {
        ({ tableName, schemaName } = resolveReference(field) || {});
    }
    return { tableName, schemaName };
}

export function parseNotationsSlots(notations: Record<string, any>) {
    return Object.entries(notations).map(([field, notation]) => ({
        name: field,
        classes: notation.layout
            ? (notation.layout as string)
                  .split(' ')
                  .map((cls) => `col-${cls}`)
                  .join(' ')
            : '',
    }));
}
